/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { Fragment } from 'react';
import styled from '@emotion/styled';
import { MDXProviderComponents } from '@mdx-js/react';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import { MDXProvider } from '@mdx-js/react';
// @ts-expect-error
import { slug as slugify } from 'github-slugger';

import { Code, CodeBlock } from '@atlaskit/code';
import { N300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';

import { SectionLink } from '@af/design-system-docs-ui';
import Props, { TSMorphProps } from '../components/props';
import isValidPropsDefinition from '../utilities/is-valid-props-definition';
import getPackageSourceLink from '../utilities/get-package-source-link';

const Header = styled.th`
  font-weight: normal;
  color: ${token('color.text.subtlest', N300)};
`;

type CodeComponentProps = { children: string };

const components: MDXProviderComponents = {
  h2: ({ children }: CodeComponentProps) => (
    <SectionLink id={slugify(children)} label={children}>
      <Heading level="h700">{children}</Heading>
    </SectionLink>
  ),
  h3: ({ children }: CodeComponentProps) => (
    <SectionLink id={slugify(children)} label={children}>
      <Heading level="h600">{children}</Heading>
    </SectionLink>
  ),
  h4: ({ children }: CodeComponentProps) => (
    <div
      css={css({
        marginTop: token('space.300', '24px'),
      })}
    >
      <Heading level="h500">{children}</Heading>
    </div>
  ),
  h5: ({ children }: CodeComponentProps) => (
    <div
      css={css({
        marginTop: token('space.200', '16px'),
      })}
    >
      <Heading level="h400">{children}</Heading>
    </div>
  ),
  code: ({ children }: CodeComponentProps) => (
    <CodeBlock text={children} language="tsx" showLineNumbers={false} />
  ),
  inlineCode: ({ children }) => <Code>{children}</Code>,
};

// this is used to generate the local nav headings on code pages
export const codeDocsHeadings = [
  {
    value: 'Installation',
    id: 'installation',
    depth: 2,
  },
];

type Props = {
  workspaceInfo: any;
  propsMdx?: any;
};

const CodeDocs = ({ workspaceInfo, propsMdx }: Props) => {
  return (
    <Fragment>
      <SectionLink
        id={codeDocsHeadings[0].id}
        label={codeDocsHeadings[0].value}
      >
        <Heading level="h700">{codeDocsHeadings[0].value}</Heading>
      </SectionLink>
      <table css={{ td: { verticalAlign: 'top' } }}>
        <tbody css={{ borderBottom: 'none' }}>
          <tr>
            <Header>Install</Header>
            <td>
              <Code>yarn add {workspaceInfo.name}</Code>
            </td>

            {workspaceInfo.repository && (
              <Fragment>
                <Header>Source</Header>
                <td>
                  <a
                    href={getPackageSourceLink(
                      workspaceInfo.repository,
                      workspaceInfo.group,
                      workspaceInfo.directoryName,
                    )}
                    target="_blank"
                  >
                    Bitbucket.org
                  </a>
                </td>
              </Fragment>
            )}
          </tr>
          <tr>
            <Header>npm</Header>
            <td>
              <a
                href={`https://www.npmjs.com/package/${workspaceInfo.name}`}
                target="_blank"
              >
                {workspaceInfo.name}
              </a>
            </td>

            <Header>Bundle</Header>
            <td>
              <a
                href={`https://unpkg.com/${workspaceInfo.name}/dist/`}
                target="_blank"
              >
                unpkg.com
              </a>
            </td>
          </tr>
        </tbody>
      </table>

      <CodeDocsPropsSection
        propsMdx={propsMdx}
        definition={workspaceInfo.definition}
      />
    </Fragment>
  );
};

interface CodeDocsPropsSectionProps {
  propsMdx: any;
  definition: any;
}

const CodeDocsPropsSection = ({
  propsMdx,
  definition,
}: CodeDocsPropsSectionProps) => {
  if (propsMdx) {
    /**
     * There is a props.mdx file we can render.
     */
    return (
      <MDXProvider components={components}>
        <MDXRenderer>{propsMdx.body}</MDXRenderer>
      </MDXProvider>
    );
  }

  if (isValidPropsDefinition(definition)) {
    /**
     * There is no props.mdx, but there is prop info from ERT.
     */
    const propsContent = 'Props';

    return (
      <Fragment>
        <SectionLink id="props" label={propsContent}>
          <Heading level="h700">{propsContent}</Heading>
        </SectionLink>
        {/* JSON.parsing JSON string input, ideally this payload should be a typed object in gql */}
        <TSMorphProps props={JSON.parse(definition)} />
      </Fragment>
    );
  }

  /**
   * If there is no prop info, we don't want to show a props section.
   */
  return null;
};

export default CodeDocs;
